/**
 * TodoRail – rechte ToDo-Leiste auf der Startseite.
 *
 * Quelle je nach User-Einstellung (/einstellungen):
 *  - eigene:  einfache ToDo-Liste (CRUD in user_todos)
 *  - jira:    offene Vorgänge des Users (holthey.atlassian.net, eigenes Token)
 *  - trello:  Karten des Boards (zentrale Trello-Zugangsdaten)
 */
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { API_BASE } from "../../api/config";
import { cacheLesen, cacheSchreiben } from "../../utils/seitenCache";
import "./TodoRail.css";

const CACHE_KEY = "home.todo";

const api = (path, opts = {}) =>
  fetch(`${API_BASE}/benutzer${path}`, {
    ...opts,
    headers: {
      "Content-Type": "application/json",
      "X-MA-Token": localStorage.getItem("maToken") || "",
      ...(opts.headers || {}),
    },
  });

const PRIO_FARBE = {
  Highest: "#ef4444", High: "#f97316", Medium: "#eab308",
  Low: "#22c55e", Lowest: "#94a3b8",
};

function istUeberfaellig(datum) {
  if (!datum) return false;
  return datum < new Date().toISOString().slice(0, 10);
}

export default function TodoRail() {
  const navigate = useNavigate();
  // Beim Neuladen zuerst das zuletzt Gesehene zeigen, dann im Hintergrund
  // aktualisieren — sonst steht die Karte sekundenlang leer da.
  const gemerkt = cacheLesen(CACHE_KEY, 24 * 3600) || {};
  const [quelle, setQuelle] = useState(gemerkt.quelle || null);
  const [todos, setTodos] = useState(gemerkt.todos || []);
  const [issues, setIssues] = useState(gemerkt.issues || []);
  const [cards, setCards] = useState(gemerkt.cards || []);
  const [neuText, setNeuText] = useState("");
  const [fehler, setFehler] = useState(null);   // {text, einstellungenLink}
  const [laden, setLaden] = useState(!gemerkt.quelle);

  const ladeFeed = useCallback(async (q) => {
    setFehler(null);
    setLaden(true);
    try {
      if (q === "eigene") {
        const r = await api("/todos");
        if (r.ok) {
          const liste = (await r.json()).todos || [];
          setTodos(liste);
          cacheSchreiben(CACHE_KEY, { quelle: q, todos: liste });
        }
      } else if (q === "jira") {
        const r = await api("/todos/jira");
        const d = await r.json().catch(() => ({}));
        if (r.status === 409) {
          setFehler({ text: "Kein Jira-Token hinterlegt.", einstellungenLink: true });
        } else if (r.status === 401) {
          setFehler({ text: "Jira-Token ungültig — bitte neu hinterlegen.", einstellungenLink: true });
        } else if (!r.ok) {
          setFehler({ text: d.detail || "Jira nicht erreichbar." });
        } else {
          setIssues(d.issues || []);
          cacheSchreiben(CACHE_KEY, { quelle: q, issues: d.issues || [] });
        }
      } else if (q === "trello") {
        const r = await api("/todos/trello");
        const d = await r.json().catch(() => ({}));
        if (!r.ok) setFehler({ text: d.detail || "Trello nicht erreichbar." });
        else {
          setCards(d.cards || []);
          cacheSchreiben(CACHE_KEY, { quelle: q, cards: d.cards || [] });
        }
      }
    } catch (_) {
      setFehler({ text: "Nicht erreichbar." });
    } finally {
      setLaden(false);
    }
  }, []);

  useEffect(() => {
    api("/einstellungen").then((r) => (r.ok ? r.json() : null)).then((d) => {
      const q = (d && d.todo_quelle) || "eigene";
      setQuelle(q);
      ladeFeed(q);
    }).catch(() => { setQuelle("eigene"); ladeFeed("eigene"); });
  }, [ladeFeed]);

  // ── eigene Liste ──────────────────────────────────────────────────────────
  const anlegen = async () => {
    const text = neuText.trim();
    if (!text) return;
    setNeuText("");
    const r = await api("/todos", { method: "POST", body: JSON.stringify({ text }) });
    if (r.ok) ladeFeed("eigene");
  };
  const abhaken = async (t) => {
    await api(`/todos/${t.id}`, { method: "PUT", body: JSON.stringify({ erledigt: !t.erledigt }) });
    ladeFeed("eigene");
  };
  const loeschen = async (t) => {
    await api(`/todos/${t.id}`, { method: "DELETE" });
    ladeFeed("eigene");
  };

  const titel = quelle === "jira" ? "🎫 Meine Jira-Vorgänge"
    : quelle === "trello" ? "📋 Trello" : "📝 ToDo";

  return (
    <aside className="todo-rail">
      <div className="todo-kopf">
        <span className="todo-titel">{titel}</span>
        <button className="todo-refresh" title="Aktualisieren" onClick={() => quelle && ladeFeed(quelle)}>↻</button>
        <button className="todo-refresh" title="Quelle ändern" onClick={() => navigate("/einstellungen")}>⚙</button>
      </div>

      {fehler && (
        <div className="todo-fehler">
          {fehler.text}{" "}
          {fehler.einstellungenLink && (
            <button className="todo-link" onClick={() => navigate("/einstellungen")}>→ Einstellungen</button>
          )}
        </div>
      )}
      {laden && <div className="todo-laden">Lädt…</div>}

      {quelle === "eigene" && !laden && (
        <>
          <div className="todo-neu">
            <input className="todo-input" placeholder="Neues ToDo…" value={neuText}
              onChange={(e) => setNeuText(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && anlegen()} />
            <button className="todo-plus" onClick={anlegen}>+</button>
          </div>
          <div className="todo-liste">
            {todos.map((t) => (
              <div key={t.id} className={`todo-item ${t.erledigt ? "erledigt" : ""}`}>
                <input type="checkbox" checked={!!t.erledigt} onChange={() => abhaken(t)} />
                <span className="todo-text">{t.text}</span>
                <button className="todo-x" onClick={() => loeschen(t)}>✕</button>
              </div>
            ))}
            {!todos.length && <div className="todo-leer">Nichts offen 🎉</div>}
          </div>
        </>
      )}

      {quelle === "jira" && !laden && !fehler && (
        <div className="todo-liste">
          {issues.map((it) => (
            <a key={it.key} className="todo-item jira" href={it.url} target="_blank" rel="noreferrer">
              <span className="todo-prio" title={it.priority}
                style={{ background: PRIO_FARBE[it.priority] || "#64748b" }} />
              <span className="todo-jira-key">{it.key}</span>
              <span className="todo-text" title={it.summary}>{it.summary}</span>
              {it.duedate && (
                <span className={`todo-due ${istUeberfaellig(it.duedate) ? "over" : ""}`}>
                  {it.duedate.slice(8, 10)}.{it.duedate.slice(5, 7)}.
                </span>
              )}
            </a>
          ))}
          {!issues.length && <div className="todo-leer">Keine offenen Vorgänge 🎉</div>}
        </div>
      )}

      {quelle === "trello" && !laden && !fehler && (
        <div className="todo-liste">
          {cards.map((c, i) => (
            <a key={i} className="todo-item jira" href={c.url} target="_blank" rel="noreferrer">
              <span className="todo-text" title={c.name}>{c.name}</span>
              <span className="todo-due">{c.liste}</span>
            </a>
          ))}
          {!cards.length && <div className="todo-leer">Keine Karten.</div>}
        </div>
      )}
    </aside>
  );
}
